/** Port of SupabaseEpisodeRepository (EpisodeRepository.swift). RLS does the gating. */
import { assertEpisodeContent, DEFAULT_VISIBLE_EPISODE_IDS, type EpisodeContent } from '@/domain';
import { supabase } from './supabase';

export interface LoadedEpisode { content: EpisodeContent; servedLocale: string }
export type LocaleFallbackPolicy = 'english' | 'strict';

const CACHE_KEY = (id: string, locale: string) => `detectea.episode.${id}.${locale}`;

function readCache(id: string, locale: string): EpisodeContent | undefined {
  try { const raw = localStorage.getItem(CACHE_KEY(id, locale)); return raw ? assertEpisodeContent(JSON.parse(raw)) : undefined; } catch { return undefined; }
}
function writeCache(id: string, locale: string, content: EpisodeContent) {
  try { localStorage.setItem(CACHE_KEY(id, locale), JSON.stringify(content)); } catch { /* quota or private mode */ }
}

export async function visibleEpisodeIDs(): Promise<Set<string>> {
  const { data, error } = await supabase().from('episodes').select('id').eq('status', 'published');
  if (error || !data) return new Set(DEFAULT_VISIBLE_EPISODE_IDS);
  return new Set(data.map((r: { id: string }) => r.id));
}

export async function loadEpisode(id: string, locale: string, policy: LocaleFallbackPolicy = 'english'): Promise<LoadedEpisode> {
  const sb = supabase();
  if (locale !== 'en') {
    const { data } = await sb.from('episode_localizations').select('episode_id,locale,content').eq('episode_id', id).eq('locale', locale).eq('status', 'published').maybeSingle();
    if (data?.content) { const c = assertEpisodeContent(data.content); writeCache(id, locale, c); return { content: c, servedLocale: locale }; }
    const cached = readCache(id, locale);
    if (cached) return { content: cached, servedLocale: locale };
    if (policy === 'strict') throw new MissingLocalizationError(id, locale);
  }
  const { data, error } = await sb.from('episodes').select('id,content').eq('id', id).eq('status', 'published').maybeSingle();
  if (data?.content) { const c = assertEpisodeContent(data.content); writeCache(id, 'en', c); return { content: c, servedLocale: 'en' }; }
  const cached = readCache(id, 'en');
  if (cached) return { content: cached, servedLocale: 'en' };
  throw new MissingPublishedEpisodeError(id, error?.message);
}

/** Locales for which EVERY given episode has a published localization (en always included). */
export async function availableLocalizationLocales(episodeIDs: string[], options: { throwOnError?: boolean } = {}): Promise<Set<string>> {
  const { data, error } = await supabase().from('episode_localizations').select('episode_id,locale').eq('status', 'published');
  // Offline: the session passes throwOnError so the cached availability survives (LanguageStore parity).
  if (error && options.throwOnError) throw error;
  const result = new Set(['en']);
  if (!data) return result;
  const byLocale = new Map<string, Set<string>>();
  for (const row of data as { episode_id: string; locale: string }[]) {
    byLocale.set(row.locale, (byLocale.get(row.locale) ?? new Set()).add(row.episode_id));
  }
  for (const [locale, ids] of byLocale) if (episodeIDs.every((id) => ids.has(id))) result.add(locale);
  return result;
}

export class MissingLocalizationError extends Error { constructor(public episodeID: string, public locale: string) { super(`No published localization ${locale} for ${episodeID}`); } }
export class MissingPublishedEpisodeError extends Error { constructor(public episodeID: string, detail?: string) { super(`Episode ${episodeID} is not published${detail ? `: ${detail}` : ''}`); } }
